import React, { useState, useEffect } from 'react';
import { Vote, Eye, X } from 'lucide-react';
import voteBoothsData from '../data/vote-booths.json';

function VoteBooths() {
  const [voteBooths, setVoteBooths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'booth_number', direction: 'asc' });
  const [selectedPDF, setSelectedPDF] = useState(null);
  const [isPDFModalOpen, setIsPDFModalOpen] = useState(false);

  useEffect(() => {
    // Load data from JSON file instead of API
    setLoading(true);
    
    // Transform the JSON data to match our format
    const transformedData = voteBoothsData.map(booth => ({
      ...booth,
      // Generate the PDF path based on the draft_voter_list filename
      pdfPath: `/assets/voter_list/${booth.voter_list}`
    }));
    
    // Sort by booth_number initially
    const sortedData = [...transformedData].sort((a, b) => {
      const aNum = parseInt(a.booth_number) || 0;
      const bNum = parseInt(b.booth_number) || 0;
      return aNum - bNum;
    });
    
    setVoteBooths(sortedData);
    setLoading(false);
  }, []);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedBooths = [...voteBooths].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    // Handle numeric sorting for number fields
    if (['male', 'female', 'other', 'total_voters', 'booth_number'].includes(sortConfig.key)) {
      const aValue = parseInt(a[sortConfig.key]) || 0;
      const bValue = parseInt(b[sortConfig.key]) || 0;
      
      if (sortConfig.direction === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    }
    
    // String sorting for text fields
    const aValue = a[sortConfig.key] || '';
    const bValue = b[sortConfig.key] || '';
    
    if (sortConfig.direction === 'asc') {
      return aValue.localeCompare(bValue);
    } else {
      return bValue.localeCompare(aValue);
    }
  });

  const handleViewPDF = (pdfPath, boothName, boothNumber) => {
    setSelectedPDF({ path: pdfPath, name: boothName, boothNumber:boothNumber });
    setIsPDFModalOpen(true);
  };

  const closePDFModal = () => {
    setIsPDFModalOpen(false);
    setSelectedPDF(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading voter booths...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="bg-gradient-to-r from-orange-600 to-orange-500 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-4">
            <Vote className="w-10 h-10 mr-4" />
            <h1 className="text-4xl font-bold">வாக்காளர் பட்டியல்</h1>
          </div>
          <p className="text-orange-100 text-lg">
            மொத்தம் {voteBooths.length} வாக்குச் சாவடிகள்
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Summary - same as before */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="text-3xl font-bold text-blue-600">
              {voteBooths.reduce((sum, booth) => sum + parseInt(booth.male || 0), 0)}
            </div>
            <div className="text-gray-600 mt-2">மொத்த ஆண்கள்</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="text-3xl font-bold text-pink-600">
              {voteBooths.reduce((sum, booth) => sum + parseInt(booth.female || 0), 0)}
            </div>
            <div className="text-gray-600 mt-2">மொத்த பெண்கள்</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="text-3xl font-bold text-purple-600">
              {voteBooths.reduce((sum, booth) => sum + parseInt(booth.other || 0), 0)}
            </div>
            <div className="text-gray-600 mt-2">மூன்றாம் பாலினம்</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="text-3xl font-bold text-green-600">
              {voteBooths.reduce((sum, booth) => sum + parseInt(booth.total_voters || 0), 0)}
            </div>
            <div className="text-gray-600 mt-2">மொத்த வாக்காளர்கள்</div>
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold">வ.எண்</th>
                  <th 
                    className="px-6 py-4 text-left text-sm font-semibold cursor-pointer hover:bg-orange-700 transition-colors"
                    onClick={() => handleSort('booth_number')}
                  >
                    <div className="flex items-center gap-2">
                      பூதஂ எண்
                      <span className="text-xs">
                        {sortConfig.key === 'booth_number' && 
                         (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </span>
                    </div>
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-sm font-semibold cursor-pointer hover:bg-orange-700 transition-colors"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-2">
                      பூதஂ பெயர்
                      <span className="text-xs">
                        {sortConfig.key === 'name' && 
                         (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </span>
                    </div>
                  </th>
                  <th 
                    className="px-6 py-4 text-center text-sm font-semibold cursor-pointer hover:bg-orange-700 transition-colors"
                    onClick={() => handleSort('male')}
                  >
                    <div className="flex items-center justify-center gap-2">
                      ஆண்
                      <span className="text-xs">
                        {sortConfig.key === 'male' && 
                         (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </span>
                    </div>
                  </th>
                  <th 
                    className="px-6 py-4 text-center text-sm font-semibold cursor-pointer hover:bg-orange-700 transition-colors"
                    onClick={() => handleSort('female')}
                  >
                    <div className="flex items-center justify-center gap-2">
                      பெண்
                      <span className="text-xs">
                        {sortConfig.key === 'female' && 
                         (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </span>
                    </div>
                  </th>
                  <th 
                    className="px-6 py-4 text-center text-sm font-semibold cursor-pointer hover:bg-orange-700 transition-colors"
                    onClick={() => handleSort('other')}
                  >
                    <div className="flex items-center justify-center gap-2">
                      மூன்றாம் பாலினம்
                      <span className="text-xs">
                        {sortConfig.key === 'other' && 
                         (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </span>
                    </div>
                  </th>
                  <th 
                    className="px-6 py-4 text-center text-sm font-semibold cursor-pointer hover:bg-orange-700 transition-colors"
                    onClick={() => handleSort('total_voters')}
                  >
                    <div className="flex items-center justify-center gap-2">
                      மொத்த வாக்காளர்கள்
                      <span className="text-xs">
                        {sortConfig.key === 'total_voters' && 
                         (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </span>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold">வாக்காளர் பட்டியல்</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedBooths.map((booth, index) => (
                  <tr key={booth.id} className="hover:bg-orange-50 transition-colors">
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium text-center">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className="inline-block bg-orange-100 text-orange-700 px-3 py-1 rounded-full font-semibold">
                        {booth.booth_number}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                      {booth.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-center">
                      <span className="inline-block bg-blue-50 text-blue-700 px-3 py-1 rounded-full font-semibold">
                        {booth.male}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-center">
                      <span className="inline-block bg-pink-50 text-pink-700 px-3 py-1 rounded-full font-semibold">
                        {booth.female}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-center">
                      <span className="inline-block bg-purple-50 text-purple-700 px-3 py-1 rounded-full font-semibold">
                        {booth.other}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-center">
                      <span className="inline-block bg-green-50 text-green-700 px-3 py-1 rounded-full font-semibold">
                        {booth.total_voters}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center justify-center">
                        {booth.pdfPath ? (
                          <button
                            onClick={() => handleViewPDF(booth.pdfPath, booth.name, booth.booth_number)}
                            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-semibold"
                          >
                            <Eye className="w-4 h-4" />
                            பார்வை
                          </button>
                        ) : (
                          <span className="text-sm text-gray-400 italic">கிடைக்கவில்லை</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden space-y-4">
          {sortedBooths.map((booth, index) => (
            <div key={booth.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold">வ.எண்: {index + 1}</span>
                  <span className="bg-white text-orange-600 px-3 py-1 rounded-full text-sm font-bold">
                    பூதஂ எண்: {booth.booth_number}
                  </span>
                </div>
                <h3 className="font-bold text-lg">{booth.name}</h3>
              </div>
              
              <div className="p-4">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-700">{booth.male}</div>
                    <div className="text-sm text-gray-600">ஆண்</div>
                  </div>
                  <div className="text-center p-3 bg-pink-50 rounded-lg">
                    <div className="text-2xl font-bold text-pink-700">{booth.female}</div>
                    <div className="text-sm text-gray-600">பெண்</div>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-700">{booth.other}</div>
                    <div className="text-sm text-gray-600">மூன்றாம் பாலினம்</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-700">{booth.total_voters}</div>
                    <div className="text-sm text-gray-600">மொத்தம்</div>
                  </div>
                </div>
                
                <div className="mt-4">
                  {booth.pdfPath ? (
                    <button
                      onClick={() => handleViewPDF(booth.pdfPath, booth.name, booth.booth_number)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-semibold"
                    >
                      <Eye className="w-5 h-5" />
                      வாக்காளர் பட்டியலைப் பார்வையிடு
                    </button>
                  ) : (
                    <div className="text-center p-4 border border-gray-200 rounded-lg">
                      <span className="text-gray-400 italic">வாக்காளர் பட்டியல் கிடைக்கவில்லை</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {voteBooths.length === 0 && !loading && (
          <div className="text-center py-16 bg-white rounded-lg shadow-md">
            <Vote className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">வாக்குச் சாவடிகள் இல்லை</h3>
            <p className="text-gray-500">தரவு ஏற்றப்படவில்லை</p>
          </div>
        )}
      </div>

      {/* PDF Modal with iframe */}
      {isPDFModalOpen && selectedPDF && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
            onClick={closePDFModal}
          ></div>
          
          {/* Modal */}
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {`${selectedPDF.name} - ${selectedPDF.boothNumber}`} - வாக்காளர் பட்டியல்
                  </h3>
                </div>
                <button
                  onClick={closePDFModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {/* PDF Viewer */}
             <div className="flex-1 overflow-hidden">
  <iframe
    src={`${selectedPDF.path}#toolbar=0&navpanes=0&scrollbar=1`}
    title={`${selectedPDF.name} - ${selectedPDF.boothNumber} - Voter List`}
    className="w-full h-full border-0"
    style={{ backgroundColor: '#f3f4f6' }}
  >
  </iframe>
</div>
              
              {/* Modal Footer */}
              <div className="flex items-center justify-between p-4 border-t bg-gray-50">
              
                <div className="flex gap-2">
                  <a
                    href={selectedPDF.path}
                    download
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-semibold"
                  >
                    Download PDF
                  </a>
                  <button
                    onClick={closePDFModal}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-semibold"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VoteBooths;